import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { URLService } from '../url.service';
import { SessionService } from '../session.service';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { ICreateCustomerSession } from './create-customer-session.interface';
import { ICreateCustomerSessionRequest } from './create-customer-session-request.interface';
import { ISession } from '../session.interface';
import { EncryptionService } from '../encryption.service';
import { ICreateCustomerSessionResponse } from './create-customer-session-response.interface';
import { IPaymentConfirmation } from './payment-confirmation.interface';
import { IPaymentConfirmationResponse } from './payment-confirmation-response.interface';
import { IPaymentConfirmationRequest } from './payment-confirmation-request.interface';
import { IPaymentAgreementRequest } from './payment-agreement-request.interface';
import { IPaymentAgreementResponse } from './payment-agreement-response.interface';
import { IPaymentTokenizeRequest } from './payment-tokenize-request.interface';
import { IPaymentTokenizeResponse } from './payment-tokenize-response.interface';
import { IPaymentScheduleOption } from './payment-schedule-option.interface';
import { IPremiumFinanceQuote } from './premium-finance-quote.interface';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  constructor(
    private sessionService: SessionService,
    private encryptionService: EncryptionService,
    private http: HttpClient,
    private urlService: URLService,
  ) {}

  createCustomerSession(
    customerSession: ICreateCustomerSession,
  ): Observable<ICreateCustomerSessionResponse | undefined> {
    const session = this.sessionService.getCurrentSession();

    if (!session) {
      console.error('Cannot create payment, no session');
      throw new Error('Cannot create payment, no session');
    }

    return this.createCustomerSessionPayload(customerSession).pipe(
      mergeMap((payload) => this.postCustomerSessionRequest(payload, session)),
    );
  }

  paymentConfirmation(
    confirmation: IPaymentConfirmation,
  ): Observable<IPaymentConfirmationResponse | undefined> {
    const session = this.sessionService.getCurrentSession();

    if (!session) {
      console.error('Cannot create confirmation, no session');
      throw new Error('Cannot create confirmation, no session');
    }

    return this.createPaymentConfirmationPayload(confirmation).pipe(
      mergeMap((payload) =>
        this.postPaymentConfirmationRequest(payload, session),
      ),
    );
  }

  paymentTokenize(
    confirmation: IPaymentConfirmation,
  ): Observable<IPaymentTokenizeResponse | undefined> {
    const session = this.sessionService.getCurrentSession();

    if (!session) {
      console.error('Cannot create confirmation, no session');
      throw new Error('Cannot create confirmation, no session');
    }

    return this.createPaymentTokenizePayload(confirmation).pipe(
      mergeMap((payload) => this.postPaymentTokenizeRequest(payload, session)),
    );
  }

  paymentAgreement(
    confirmation: IPaymentConfirmation,
  ): Observable<IPaymentAgreementResponse | undefined> {
    const session = this.sessionService.getCurrentSession();

    if (!session) {
      console.error('Cannot create agreement, no session');
      throw new Error('Cannot create agreement, no session');
    }

    return this.createPaymentAgreementPayload(confirmation).pipe(
      mergeMap((payload) => this.postPaymentAgreementRequest(payload, session)),
    );
  }

  completeAgreement(
    paymentIntentId: string,
    setupIntentId: string,
    paymentLinkToken?: string,
  ): Observable<IPaymentAgreementResponse | undefined> {
    const session = this.sessionService.getCurrentSession();

    if (!session) {
      console.error('Cannot complete agreement, no session');
      throw new Error('Cannot complete agreement, no session');
    }

    return this.http
      .post<IPaymentAgreementResponse>(
        this.urlService.agreementComplete(),
        { paymentIntentId, setupIntentId, paymentLinkToken },
        {
          headers: {
            authorization: session.authorizationString,
            'X-Region': session.region,
          },
        },
      )
      .pipe(catchError((err) => of(err?.error)));
  }

  completeAgreementFee(
    paymentIntentId: string,
    setupIntentId: string,
    feePaymentIntentId: string,
    paymentLinkToken?: string,
  ): Observable<IPaymentAgreementResponse | undefined> {
    const session = this.sessionService.getCurrentSession();

    if (!session) {
      console.error('Cannot complete fee agreement, no session');
      throw new Error('Cannot complete fee agreement, no session');
    }

    return this.http
      .post<IPaymentAgreementResponse>(
        this.urlService.agreementCompleteFee(),
        { paymentIntentId, setupIntentId, feePaymentIntentId, paymentLinkToken },
        {
          headers: {
            authorization: session.authorizationString,
            'X-Region': session.region,
          },
        },
      )
      .pipe(catchError((err) => of(err?.error)));
  }

  isAcssSupported(): Observable<boolean> {
    const session = this.sessionService.getCurrentSession();

    if (!session) {
      console.error('Cannot check ACSS support, no session');
      throw new Error('Cannot check ACSS support, no session');
    }

    return this.http.get<{ acssSupported: boolean }>(this.urlService.checkAcss(), {
      headers: {
        Authorization: session.authorizationString,
        'X-Region': session.region,
      },
    }).pipe(
      mergeMap(response => of(response.acssSupported)),
      catchError((err) => {
        console.error('Failed to check ACSS support:', err);
        return of(false);
      }),
    );
  }

  // PPD-1300 — fetch the FIRSTLink indicative quote for the given RTP link token.
  // Returns { available: false } silently on any error so the PF option is simply hidden.
  getPremiumFinanceQuote(paymentLinkToken: string): Observable<IPremiumFinanceQuote> {
    const session = this.sessionService.getCurrentSession();
    if (!session) {
      return of({ available: false } as IPremiumFinanceQuote);
    }
    return this.http.post<IPremiumFinanceQuote>(
      this.urlService.premiumFinanceQuote(),
      { paymentLinkToken },
      {
        headers: {
          authorization: session.authorizationString,
          'X-Region': session.region,
        },
      },
    ).pipe(catchError(() => of({ available: false })));
  }

  getActivePaymentScheduleRules(): Observable<IPaymentScheduleOption[]> {
    const session = this.sessionService.getCurrentSession();
    if (!session) {
      throw new Error('Cannot fetch payment schedule rules, no session');
    }
    return this.http.get<{ options: IPaymentScheduleOption[] }>(
      this.urlService.paymentScheduleRules(),
      {
        headers: {
          authorization: session.authorizationString,
          'X-Region': session.region,
        },
      },
    ).pipe(map((res) => res.options ?? []));
  }

  private postCustomerSessionRequest(
    payload: ICreateCustomerSessionRequest,
    session: ISession,
  ): Observable<ICreateCustomerSessionResponse | undefined> {
    return this.http
      .post<ICreateCustomerSessionResponse>(
        this.urlService.customerSession(),
        payload,
        {
          headers: {
            authorization: session.authorizationString,
            'X-Region': session.region,
          },
        },
      )
      .pipe(
        catchError((err) => {
          return of(err?.error);
        }),
      );
  }

  private postPaymentConfirmationRequest(
    payload: IPaymentConfirmationRequest,
    session: ISession,
  ): Observable<IPaymentConfirmationResponse | undefined> {
    return this.http
      .post<IPaymentConfirmationResponse>(
        this.urlService.paymentConfirmation(),
        payload,
        {
          headers: {
            authorization: session.authorizationString,
            'X-Region': session.region,
          },
        },
      )
      .pipe(catchError((err) => of(err?.error)));
  }

  private postPaymentTokenizeRequest(
    payload: IPaymentTokenizeRequest,
    session: ISession,
  ): Observable<IPaymentTokenizeResponse | undefined> {
    return this.http
      .post<IPaymentTokenizeResponse>(
        this.urlService.paymentTokenize(),
        payload,
        {
          headers: {
            authorization: session.authorizationString,
            'X-Region': session.region,
          },
        },
      )
      .pipe(catchError((err) => of(err?.error)));
  }

  private postPaymentAgreementRequest(
    payload: IPaymentAgreementRequest,
    session: ISession,
  ): Observable<IPaymentAgreementResponse | undefined> {
    return this.http
      .post<IPaymentAgreementResponse>(
        this.urlService.agreementConfirmation(),
        payload,
        {
          headers: {
            authorization: session.authorizationString,
            'X-Region': session.region,
          },
        },
      )
      .pipe(catchError((err) => of(err?.error)));
  }

  private createCustomerSessionPayload(
    customerSession: ICreateCustomerSession,
  ): Observable<ICreateCustomerSessionRequest> {
    const {
      amount,
      insuredName,
      custom,
      isTokenize,
      isAppliedEpic,
      paymentWithToken,
      ...payload
    } = customerSession;

    console.log(customerSession);

    const rawInput: Record<string, string | undefined> = {};
    for (const [key, value] of Object.entries(customerSession)) {
      if (typeof value === 'string' || value === undefined) {
        rawInput[key] = value;
      }
    }

    return of({
      customerSession: {
        insuredName,
        amount: parseFloat(amount),
        isTokenize,
        custom,
        isAppliedEpic,
        paymentWithToken,
        ...payload,
        rawInput,
      },
    } as ICreateCustomerSessionRequest);
  }

  private createPaymentConfirmationPayload(
    confirmation: IPaymentConfirmation,
  ): Observable<IPaymentConfirmationRequest> {
    return of({
      paymentConfirmation: {
        confirmationToken: confirmation.confirmationToken,
        paymentIntentId: confirmation.paymentIntentId,
      },
    } as IPaymentConfirmationRequest);
  }

  private createPaymentTokenizePayload(
    confirmation: IPaymentConfirmation,
  ): Observable<IPaymentTokenizeRequest> {
    return of({
      paymentTokenize: {
        confirmationToken: confirmation.confirmationToken,
        paymentIntentId: confirmation.paymentIntentId,
      },
    } as IPaymentTokenizeRequest);
  }

  private createPaymentAgreementPayload(
    confirmation: IPaymentConfirmation,
  ): Observable<IPaymentAgreementRequest> {
    return of({
      paymentAgreement: {
        confirmationToken: confirmation.confirmationToken,
        paymentIntentId: confirmation.paymentIntentId,
        recaptcha: confirmation.recaptcha,
        verificationId: confirmation.verificationId,
        paymentScheduleRuleId: confirmation.paymentScheduleRuleId ?? null,
        scheduledDate: confirmation.scheduledDate ?? null,
        paymentLinkToken: confirmation.paymentLinkToken,
      },
    } as IPaymentAgreementRequest);
  }
}
